export interface ContactEntry {
  /** Mono label above the name, e.g. "Dean, Admissions". */
  role: string;
  name: string;
  /** Second line under the name, where the source publishes one. */
  detail?: string;
  phone?: string;
  email?: string;
  /** One extra labelled line — opening hours, turnaround, whatever the desk publishes. */
  extra?: { label: string; value: string };
}

interface ContactBandProps {
  title: string;
  entries: ContactEntry[];
  /** Anchor id, where a side rail links to the section. */
  id?: string;
}

/**
 * The "Who to contact" band, shared by /admissions, /placements and
 * /examinations.
 *
 * One contact per row rather than three cards across. The card grid gave every
 * contact identical weight, so nothing told a reader who to call first — the
 * job the block actually has. Reading order now carries that, which is why the
 * sentence that used to explain it ("reach the Dean first…") is not needed.
 *
 * It also retires a layout bug: three fixed columns left /examinations, which
 * has two desks, showing an empty third column. Rows stack for any count.
 */
export function ContactBand({ title, entries, id }: ContactBandProps) {
  return (
    <section id={id} className="section section-tight contact-band">
      <div className="container">
        <div className="kicker">Who to contact</div>
        <h2 className="contact-band-h2">{title}</h2>
        <ul className="contact-rows">
          {entries.map((c) => (
            <li key={c.name} className="contact-row">
              <div className="contact-row-who">
                <div className="contact-row-role">{c.role}</div>
                <div className="contact-row-name">{c.name}</div>
                {c.detail && <div className="contact-row-detail">{c.detail}</div>}
              </div>
              <div className="contact-row-reach">
                {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
                {c.extra && (
                  <span className="contact-row-extra">
                    {c.extra.label} · {c.extra.value}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
